/**
 * Module dependencies.
 */

import { Svg } from '@untile/react-core/components/svg';
import isEmpty from 'lodash/isEmpty';
import React, { ReactNode, forwardRef } from 'react';
import checkIcon from 'src/assets/16/check.svg';
import { ErrorMessage } from 'src/components/core/forms/styles';
import { Text } from 'src/components/core/text';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

/**
 * Export `CheckboxProps` type.
 */

export type CheckboxProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  label: ReactNode;
} & Omit<React.HTMLProps<HTMLInputElement>, 'as' | 'label' | 'type'>;

/**
 * `CheckmarkWrapper` styled component.
 */

const CheckmarkWrapper = styled.span`
  background-color: var(--checkbox-background-color);
  border: 1px solid var(--checkbox-border-color);
  border-radius: 4px;
  grid-area: checkbox;
  height: var(--checkbox-size);
  position: relative;
  transition: var(--transition-fast);
  transition-property: background-color, border-color;
  width: var(--checkbox-size);
`;

/**
 * `StyledErrorMessage` styled component.
 */

const StyledErrorMessage = styled(ErrorMessage)`
  grid-area: error;
  margin-left: calc(0px - var(--checkbox-column-gap) - var(--checkbox-size));
  padding: 0;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  color: var(--checkbox-icon-color);
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: opacity var(--transition-fast);
`;

/**
 * `Label` styled component.
 */

const Label = styled.label<
  {
    hasError?: boolean;
    hasLabel?: boolean;
  } & Pick<CheckboxProps, 'disabled'>
>`
  --checkbox-background-color: var(--color-grey200);
  --checkbox-border-color: var(--color-grey400);
  --checkbox-icon-color: transparent;
  --checkbox-label-color: var(--text-color);

  align-items: flex-start;
  color: var(--checkbox-label-color);
  cursor: pointer;
  display: grid;
  padding: 8px 0;
  position: relative;
  width: max-content;

  ${ifProp(
    'hasLabel',
    css`
      grid-column-gap: var(--checkbox-column-gap);
      grid-template-areas: 'checkbox label';
      grid-template-columns: var(--checkbox-size) 1fr;
      width: 100%;
    `
  )};

  &:focus-within,
  &:hover {
    --checkbox-background-color: var(--color-grey300);
    --checkbox-border-color: var(--color-grey500);

    input:checked ~ ${CheckmarkWrapper} {
      --checkbox-background-color: var(--color-blue500);
      --checkbox-border-color: var(--color-blue500);
    }
  }

  &:active {
    --checkbox-background-color: var(--color-grey500);
    --checkbox-border-color: var(--color-grey500);
  }

  input:checked {
    ~ ${CheckmarkWrapper} {
      --checkbox-background-color: var(--color-blue400);
      --checkbox-border-color: var(--color-blue400);
      --checkbox-icon-color: var(--color-blue100);
    }

    &[disabled] ~ ${CheckmarkWrapper} {
      --checkbox-background-color: var(--color-blue400);
      --checkbox-border-color: var(--color-blue400);
      --checkbox-icon-color: var(--color-blue600);
    }
  }

  ${ifProp(
    { hasError: true, hasLabel: true },
    css`
      grid-row-gap: 8px;
      grid-template-areas:
        'checkbox label'
        '.        error';
    `
  )}

  ${ifProp(
    'disabled',
    css`
      --checkbox-background-color: var(--color-grey200);
      --checkbox-border-color: var(--color-grey200);
      --checkbox-label-color: var(-color-grey700);

      cursor: default;
      pointer-events: none;
    `
  )}
`;

/**
 * `Input` styled component.
 */

const Input = styled.input`
  cursor: pointer;
  grid-area: checkbox;
  height: var(--checkbox-size);
  opacity: 0;
  width: var(--checkbox-size);
  z-index: 1;
`;

/**
 * `LabelText` styled component.
 */

const LabelText = styled(Text).attrs({
  variant: 'paragraph2'
})<Pick<CheckboxProps, 'disabled'>>`
  align-self: center;
  color: ${ifProp('disabled', 'var(--color-grey700)', 'var(--color-grey900)')};
  grid-area: label;
  overflow-wrap: anywhere;
  transition: var(--transition-default);
  transition-property: color, opacity;
`;

/**
 * Export `Checkbox` component.
 */

export const Checkbox = forwardRef(
  ({ className, disabled, error, id, label, name, ...props }: CheckboxProps, ref: any) => {
    return (
      <Label className={className} disabled={disabled} hasError={!isEmpty(error)} hasLabel={!!label}>
        <Input disabled={disabled} id={id ?? name} name={name} ref={ref} type={'checkbox'} {...props} />

        {!!label && <LabelText disabled={disabled}>{label}</LabelText>}

        {!isEmpty(error) && <StyledErrorMessage>{error}</StyledErrorMessage>}

        <CheckmarkWrapper>
          <Icon icon={checkIcon} size={'16px'} />
        </CheckmarkWrapper>
      </Label>
    );
  }
);

/**
 * `Checkbox` display name.
 */

Checkbox.displayName = 'Checkbox';
